import React, { FC, ReactNode, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AuthenticationContext, ClaimsContext, ResourcesLoader, TermsOfUseModal as TermsOfUseModalPk, redirectTo } from 'omni-package-react-core';
import { useSitecoreJssQuery } from 'omni-package-react-cms';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountConsents, AccountConsentsResource, AccountConsentsResourceInfo, AcceptTermsOfUseResource } from 'we-package-react-account';
import { openModalByName, requestResourceByName, useScopedSelector } from 'ia-react-core';
import { useDispatch } from 'react-redux';
import ErrorLayout from '~/layouts/ErrorLayout';
import { useLogout } from '~/hooks/useLogout';
import { getOmniLogger } from 'omni-package-react-logging';
import getTokenManager from '~/utils/getTokenManager';
import Constants from '~/app/Constants';
import { refreshToken } from '../ActivityManager/refreshToken';

export interface TermsOfUseModalProps { children: ReactNode }

const DIAG_LOG_PREFIX = '[DIAG][TermsOfUseModal]';

const TermsOfUseModal: FC<TermsOfUseModalProps> = ({ children }) => {
  const ctx = useContext(ClaimsContext);
  const url = useSitecoreJssQuery('/terms-of-use');
  const tokenManager = useMemo(() => getTokenManager(), []);
  const { authenticationInfo } = useContext(AuthenticationContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  const termsOfUseConsents: AccountConsents = useScopedSelector(AccountConsentsResourceInfo.dataDst);
  const isTermsOfUseOpen: boolean = useScopedSelector('/App.termsOfUseModal.isOpened');
  const [isTermsOfUseAccepted, setIsTermsOfUseAccepted] = useState(termsOfUseConsents?.data?.termsOfUseConsent);
  const lastTechnicalFailureLogKeyRef = useRef('');

  const logger = getOmniLogger('we-client-space-activity-manager');

  const handleTechnicalFailure = useCallback((resourceName: string, failure?: unknown) => {
    const response = (failure as { response?: { status?: number } } | undefined)?.response;
    const status = response?.status;
    const rawPathname = location.pathname;
    const pathname = encodeURIComponent(rawPathname);

    const failureDiag = {
      resourceName,
      pathname,
      rawPathname,
      isAuthenticated: authenticationInfo?.isAuthenticated,
      isAuthenticationProcessed: authenticationInfo?.isAuthenticationProcessed,
      status,
    };

    const logKey = JSON.stringify(failureDiag);
    if (lastTechnicalFailureLogKeyRef.current !== logKey) {
      lastTechnicalFailureLogKeyRef.current = logKey;
      // eslint-disable-next-line no-console
      console.warn(`${DIAG_LOG_PREFIX} resource failure redirecting to technical error ${logKey}`);
    }

    redirectTo(Constants.PagesUrl.TechnicalErrorPage, navigate);
  }, [authenticationInfo?.isAuthenticated, authenticationInfo?.isAuthenticationProcessed, location.pathname, navigate]);

  useEffect(() => {
    if (termsOfUseConsents && !isTermsOfUseOpen) {
      dispatch(openModalByName('termsOfUseModal'));
    }
  }, [dispatch, termsOfUseConsents, isTermsOfUseOpen]);
  if (ctx?.isTermsOfUseConsents || isTermsOfUseAccepted || termsOfUseConsents?.data?.termsOfUseConsent) {
    return <>{children}</>;
  }
  return (
    <ErrorLayout name="Error" lob="corpo">
      <ResourcesLoader>
        <ResourcesLoader.Resources>
          <AccountConsentsResource onFailure={(failure) => handleTechnicalFailure('AccountConsentsResource', failure)} autoRequest />
          <AcceptTermsOfUseResource
            termsOfUseConsents={termsOfUseConsents}
            onFailure={(failure) => handleTechnicalFailure('AcceptTermsOfUseResource', failure)}
            onSuccess={() => {
              setIsTermsOfUseAccepted(true);
              refreshToken(authenticationInfo.isAuthenticationProcessed, logger, tokenManager);
            }}
          />
        </ResourcesLoader.Resources>
        <ResourcesLoader.Content>
          <TermsOfUseModalPk
            url={url}
            showGlobalConsents={termsOfUseConsents?.data.showTermsOfUse}
            customLogout={logout}
            onConfirm={() => {
              dispatch(requestResourceByName('/App.features.termsOfUse.acceptTermsOfUseResource'));
            }}
          />
        </ResourcesLoader.Content>
      </ResourcesLoader>
    </ErrorLayout>
  );
};
export default TermsOfUseModal;
